import React from "react";
import { Icon } from "@iconify/react";
import Card from "../Card";
import { Typography } from "../Typography";
import {
  IDEA_FLOW_STAGE_NAMES,
  getIdeaFlowStageTheme,
  getIdeaStatusBadgeClasses,
  formatIdeaStatusLabel,
} from "../../Container/IdeasReview/utils";

const IdeaFlowCard = ({
  idea,
  onClick,
  hrefLabel = "View",
  onStudentClick,
  showMeta = false,
  onActivate,
  activating = false,
  onContinue,
  onSelectMentors,
}) => {
  const statusBadge = idea.status ? getIdeaStatusBadgeClasses(idea.status) : null;
  const stageTheme = getIdeaFlowStageTheme(idea.stepName);
  const stepProgress = Math.min(
    IDEA_FLOW_STAGE_NAMES.length,
    Math.max(1, idea.currentStep ?? 1)
  );

  return (
    <Card
      role="link"
      tabIndex={0}
      bodyClassName="p-0 flex flex-col h-full"
      className={`group flex flex-col h-full min-w-0 w-full cursor-pointer border border-slate-200/90 shadow-sm ring-1 ring-transparent transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${stageTheme.ring} focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-slate-100">
        <img
          src={idea.image}
          alt={idea.title}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
          {idea.stepName && (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold shadow-md ${stageTheme.badge}`}
            >
              <Icon icon="lucide:layers" className="h-3.5 w-3.5" />
              {idea.stepName}
            </span>
          )}
          {statusBadge && (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold capitalize backdrop-blur-sm ${statusBadge.bg} ${statusBadge.text} ${statusBadge.border}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${statusBadge.dot}`} />
              {formatIdeaStatusLabel(idea.status)}
            </span>
          )}
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <div className="flex gap-1">
            {IDEA_FLOW_STAGE_NAMES.map((name, index) => (
              <div
                key={name}
                title={name}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  index < stepProgress ? stageTheme.bar : "bg-white/35"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        {idea.submissionDate && (
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <Icon icon="lucide:calendar" className="h-3.5 w-3.5 shrink-0" />
            <span>Submitted {idea.submissionDate}</span>
          </div>
        )}
        <div className="flex flex-col gap-1.5 min-h-0">
          <Typography
            as="h3"
            className="text-lg font-bold text-slate-900 line-clamp-1 group-hover:text-brand-700 transition-colors"
          >
            {idea.title}
          </Typography>
          <Typography as="p" className="text-sm text-slate-600 line-clamp-2 leading-relaxed">
            {idea.description || "No problem statement provided."}
          </Typography>
          {showMeta && (idea.category || idea.primaryImpact) && (
            <div className="flex flex-wrap gap-2 pt-1">
              {idea.category && (
                <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-600 max-w-full">
                  <Icon icon="lucide:tag" className="h-3 w-3 shrink-0" />
                  <span className="truncate">{idea.category}</span>
                </span>
              )}
              {idea.primaryImpact && (
                <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                  <Icon icon="lucide:target" className="h-3 w-3 shrink-0" />
                  {idea.primaryImpact}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-auto flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/80 px-5 py-3">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          {(idea.tags || []).map((tag, index) => {
            const isStudentTag = tag.id?.startsWith("student-") && idea.studentId && onStudentClick;
            const isMentorTag = tag.role === "mentor" || tag.id?.startsWith("mentor-");
            const TagWrapper = isStudentTag ? "button" : "span";
            return (
              <TagWrapper
                key={tag.id ?? index}
                type={isStudentTag ? "button" : undefined}
                onClick={
                  isStudentTag
                    ? (e) => {
                        e.stopPropagation();
                        onStudentClick(idea);
                      }
                    : undefined
                }
                className={`inline-flex items-center gap-1.5 rounded-full border bg-white px-2 py-1 text-xs font-medium shadow-sm ${
                  isMentorTag
                    ? "border-emerald-200 text-emerald-800"
                    : "border-slate-200 text-slate-700"
                } ${
                  isStudentTag ? "hover:border-brand-300 hover:text-brand-700 cursor-pointer" : ""
                }`}
              >
                {tag.avatar ? (
                  <img
                    src={tag.avatar}
                    alt={tag.label}
                    className="h-5 w-5 rounded-full object-cover"
                  />
                ) : (
                  <Icon
                    icon={tag.icon || "lucide:user"}
                    className={`h-4 w-4 ${isMentorTag ? "text-emerald-600" : "text-brand-500"}`}
                  />
                )}
                <span className="truncate max-w-[100px]">{tag.label}</span>
              </TagWrapper>
            );
          })}
        </div>
        {onContinue ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onContinue();
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-violet-300 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 transition-colors hover:bg-violet-100 shrink-0"
          >
            <Icon icon="lucide:pencil-line" className="h-4 w-4" />
            Continue
          </button>
        ) : onSelectMentors ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelectMentors();
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 shrink-0"
          >
            <Icon icon="lucide:user-plus" className="h-4 w-4" />
            Select Mentors
          </button>
        ) : onActivate ? (
          <button
            type="button"
            disabled={activating}
            onClick={(e) => {
              e.stopPropagation();
              onActivate();
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-50 shrink-0"
          >
            <Icon icon="lucide:play-circle" className="h-4 w-4" />
            {activating ? "Activating..." : "Activate"}
          </button>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 opacity-0 transition-opacity group-hover:opacity-100 shrink-0">
            {hrefLabel}
            <Icon icon="lucide:arrow-right" className="h-3.5 w-3.5" />
          </span>
        )}
      </div>
    </Card>
  );
};

export default IdeaFlowCard;
